import type { DocPageContent } from '../docs-content'

const page = {
  order: 10,
  slug: 'properties',
  title: 'Propiedades del clip',
  description:
    'Transformar, recortar, opacidad y mezcla, y los controles específicos de cada tipo de clip.',
  category: 'Edición principal',
  related: ['audio', 'text-captions-subtitles', 'effects-color', 'keyframes'],
  sections: [
    {
      title: 'Cómo la selección determina el panel',
      blocks: [
        {
          kind: 'list',
          items: [
            'Selecciona un clip para editar todo su conjunto de propiedades.',
            'Selecciona varios clips para cambiar una propiedad compartida en todos a la vez.',
            'El panel muestra pestañas solo para los grupos de propiedades que aplican a la selección actual.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Un valor **Mixto** significa que los clips seleccionados no comparten actualmente ese ajuste; cambiarlo aplica un mismo valor a todos.',
        },
      ],
    },
    {
      title: 'Controles compartidos por los clips visuales',
      blocks: [
        {
          kind: 'list',
          items: [
            '**Transformar**: posición, tamaño, rotación y punto de anclaje.',
            '**Recortar**: recorta cada borde hacia adentro, con un control de suavizado para el borde del recorte.',
            '**Opacidad** y **Mezcla**: desvanece un clip y elige cómo se compone sobre las capas de abajo.',
            '**Video**: velocidad de reproducción y otros ajustes de tiempo relacionados para clips de video.',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Muchos controles numéricos se pueden animar; busca el interruptor de fotograma clave junto al valor.',
        },
      ],
    },
    {
      title: 'Controles específicos por tipo',
      blocks: [
        {
          kind: 'table',
          headers: ['Tipo de clip', 'Controles'],
          rows: [
            ['Audio', 'Ganancia, entrada/salida gradual (fade), tono y un ecualizador por clip (consulta Edición de audio).'],
            [
              'Texto',
              'Contenido, fuente, tamaño, color, alineación, fondo, espaciado, efectos y animación (consulta la página de Texto).',
            ],
            ['Forma', 'Tipo de forma, trazado, relleno, contorno, máscara y difuminado.'],
            ['Subtítulo', 'Tiempos y texto de los cues, además de preajustes de estilo de subtítulos.'],
            ['Color', 'Ruedas, curvas y herramientas de corrección de color (consulta Efectos y color).'],
          ],
        },
      ],
    },
    {
      title: 'Marcadores',
      blocks: [
        {
          kind: 'paragraph',
          text: 'El panel de propiedades enumera todos los marcadores del proyecto bajo el encabezado **Marcadores**, ordenados por tiempo. La lista siempre está disponible: se muestra debajo de los ajustes del lienzo cuando no hay nada seleccionado, y debajo del editor de marcadores cuando hay uno seleccionado, para que puedas explorar y llegar a tus marcadores sin importar qué esté seleccionado.',
        },
        {
          kind: 'list',
          items: [
            'Cada fila muestra el color del marcador, su etiqueta (o un nombre predeterminado si no tiene ninguna) y su código de tiempo.',
            'Selecciona una fila para convertirla en el marcador activo y llevar el cursor de reproducción a ese punto.',
            'Usa el ícono de papelera en una fila para eliminar ese marcador individual.',
            '**Borrar todos** elimina todos los marcadores a la vez.',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Presiona `M` para colocar un marcador en el cursor de reproducción. Cuando la lista está vacía, se muestra esta sugerencia.',
        },
        {
          kind: 'paragraph',
          text: 'Seleccionar un marcador abre su editor sobre la lista, donde puedes cambiar la posición del **Fotograma**, editar la **Etiqueta** y definir el **Color** desde una muestra preestablecida o restablecerlo al predeterminado. El campo **Tiempo** muestra el código de tiempo del marcador de solo lectura, y **Eliminar marcador** lo quita.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
